import { useParams, Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { blogPosts } from '@/mocks/blog';
import { danielBCFernandes } from '@/mocks/author';
import ReadingProgress from './components/ReadingProgress';
import TableOfContents from './components/TableOfContents';
import ShareButtons from './components/ShareButtons';
import ReadingTimeIndicator from './components/ReadingTimeIndicator';
import MostReadPosts from './components/MostReadPosts';
import CategoryList from './components/CategoryList';
import AuthorBio from './components/AuthorBio';
import PostFAQ from './components/PostFAQ';
import RelatedFAQ from './components/RelatedFAQ';
import { useBlogSEO } from './hooks/useBlogSEO';
import { categoryToSlug } from '@/utils/blogUtils';
import { blogFAQs } from '@/mocks/blog-faq';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { useCardStagger } from '@/hooks/useCardStagger';
import PacotesBox from '@/components/feature/PacotesBox';

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const post = blogPosts.find((p) => p.slug === slug);

  useBlogSEO(post);

  /* Animations */
  const { ref: heroRef, isVisible: heroVisible } = useScrollAnimation({ threshold: 0.05 });
  const { ref: articleRef, isVisible: articleVisible } = useScrollAnimation({ threshold: 0.05 });
  const { ref: relatedHeaderRef, isVisible: relatedHeaderVisible } = useScrollAnimation({ threshold: 0.1 });
  const { containerRef: relatedGridRef, visibleCount: relatedVisible } = useCardStagger(3, { staggerMs: 100 });

  if (!post) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900">
        <Header />
        <div className="max-w-4xl mx-auto px-6 py-40 text-center">
          <h1 className="font-heading font-bold text-4xl text-secondary mb-6">Post não encontrado</h1>
          <Link to="/blog" className="inline-block bg-primary text-white px-8 py-3 rounded-full font-body font-semibold hover:bg-opacity-90 transition-all whitespace-nowrap cursor-pointer">
            Voltar ao Blog
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedPosts = blogPosts.filter((p) => p.id !== post.id).slice(0, 3);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <Header />
      <ReadingProgress />

      {/* ── Hero ── */}
      <section ref={heroRef} className="relative h-[540px] flex items-end overflow-hidden mt-[72px]">
        <div className="absolute inset-0">
          <img
            src={post.image}
            alt={post.title}
            title={post.title}
            className={`w-full h-full object-cover object-center transition-all duration-1000 ${heroVisible ? 'scale-100' : 'scale-110'}`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/15" />
        </div>

        <div className="relative z-10 w-full max-w-5xl mx-auto px-6 pb-14">
          {/* Breadcrumb */}
          <nav
            aria-label="Breadcrumb"
            className={`flex items-center gap-2 text-white/60 text-xs font-body mb-4 transition-all duration-600 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
          >
            <Link to="/" className="hover:text-white transition-colors cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line text-white/40"></i>
            <Link to="/blog" className="hover:text-white transition-colors cursor-pointer">Blog</Link>
            <i className="ri-arrow-right-s-line text-white/40"></i>
            <span className="text-white/80 truncate max-w-xs">{post.category}</span>
          </nav>

          <Link
            to={`/blog/categoria/${categoryToSlug(post.category)}`}
            className={`inline-block bg-primary text-white px-4 py-1.5 rounded-full font-body text-xs font-bold uppercase tracking-wide mb-5 hover:bg-opacity-90 transition-all cursor-pointer whitespace-nowrap duration-700 delay-100 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          >
            {post.category}
          </Link>

          <h1
            className={`font-heading font-bold text-4xl md:text-5xl text-white leading-tight mb-6 max-w-4xl transition-all duration-700 delay-150 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            {post.title}
          </h1>

          <div
            className={`flex flex-wrap items-center gap-5 text-white/75 font-body text-sm transition-all duration-700 delay-200 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          >
            <span className="flex items-center gap-2">
              <div className="w-7 h-7 flex items-center justify-center">
                <img
                  src="/img/stib/logo-stib-3d.png"
                  alt="STIB"
                  className="w-7 h-7 rounded-full object-contain bg-white/90 p-0.5 border border-white/30"
                />
              </div>
              Equipe STIB
            </span>
            <span className="flex items-center gap-1.5">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-calendar-line text-sm text-white/60"></i>
              </div>
              {post.date}
            </span>
            <span className="flex items-center gap-1.5">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-time-line text-sm text-white/60"></i>
              </div>
              {post.readTime} de leitura
            </span>
          </div>
        </div>
      </section>

      {/* ── Main Content ── */}
      <div
        ref={articleRef}
        className={`max-w-7xl mx-auto px-6 py-16 transition-all duration-700 ${articleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_288px] gap-14 items-start">

          {/* Article */}
          <article>
            <p className="font-body text-xl text-gray-600 leading-relaxed mb-8 pb-8 border-b border-gray-100 font-normal italic">
              {post.excerpt}
            </p>

            <div className="mb-10">
              <ShareButtons title={post.title} slug={post.slug} />
            </div>

            <div className="blog-content" dangerouslySetInnerHTML={{ __html: post.content }} />

            <div className="mt-12 pt-8 border-t border-gray-100">
              <ShareButtons title={post.title} slug={post.slug} />
            </div>

            <PostFAQ faq={blogFAQs[post.id] ?? []} postTitle={post.title} postSlug={post.slug} />
            <RelatedFAQ postId={post.id} postCategory={post.category} />
            <div className="mt-12 p-6 rounded-2xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10">
              <p className="font-heading font-bold text-secondary dark:text-white mb-2">Escrito pela Equipe STIB</p>
              <p className="font-body text-sm text-gray-600 dark:text-white/70 leading-relaxed">Conteúdo produzido pela equipe da STIB, que opera palestra silenciosa, fones sem fio e tradução simultânea em eventos corporativos por todo o Brasil. <a href="/quem-somos" className="text-primary font-semibold hover:underline">Conheça a equipe</a>.</p>
            </div>

            {/* ── CTA Box ── */}
            <div className="mt-12 rounded-3xl overflow-hidden relative">
              <div className="p-10" style={{ background: 'linear-gradient(135deg, #384774 0%, #1a3060 100%)' }}>
                <div
                  className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-10"
                  style={{ background: '#18c7f4', transform: 'translate(30%, -30%)' }}
                />
                <p className="font-body text-xs font-bold uppercase tracking-widest text-primary mb-3">
                  Pronto para o próximo passo?
                </p>
                <h3 className="font-heading font-bold text-3xl text-white mb-4 relative z-10">
                  Leve essa tecnologia para o seu evento
                </h3>
                <p className="font-body text-white/70 mb-8 max-w-xl relative z-10">
                  A STIB atende todo o Brasil com suporte técnico presencial e equipamentos profissionais. Fale com a equipe agora mesmo.
                </p>
                <div className="flex flex-wrap gap-4 relative z-10">
                  <Link to="/contato" className="inline-flex items-center gap-2 bg-primary text-white px-8 py-4 rounded-full font-body font-semibold hover:bg-opacity-90 transition-all whitespace-nowrap cursor-pointer">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-send-plane-fill text-base"></i>
                    </div>
                    Solicitar orçamento
                  </Link>
                  <Link to="/portfolio" className="inline-flex items-center gap-2 border-2 border-white/30 text-white px-8 py-4 rounded-full font-body font-semibold hover:border-white/60 transition-all whitespace-nowrap cursor-pointer">
                    Ver portfólio
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-base"></i>
                    </div>
                  </Link>
                </div>
              </div>
            </div>
          </article>

          {/* ── Sidebar ── */}
          <aside className="hidden lg:block">
            <TableOfContents content={post.content} />
            <div className="mt-6">
              <ReadingTimeIndicator content={post.content} />
            </div>
            <div className="mt-6">
              <MostReadPosts currentPostId={post.id} />
            </div>
            <div className="mt-6">
              <CategoryList currentCategory={post.category} />
            </div>
            <div className="mt-8 rounded-2xl p-6 border border-primary/20 bg-primary/5">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary/10 mb-3">
                <i className="ri-phone-line text-primary text-lg"></i>
              </div>
              <p className="font-heading font-bold text-secondary text-lg mb-2 leading-tight">
                Quer um orçamento rápido?
              </p>
              <p className="font-body text-sm text-gray-500 mb-4 leading-relaxed">
                Atendemos todo o Brasil com suporte técnico especializado.
              </p>
              <a href="tel:+5511917776227" className="flex items-center justify-center gap-2 w-full bg-primary text-white py-3 rounded-full font-body font-semibold text-sm hover:bg-opacity-90 transition-all cursor-pointer whitespace-nowrap">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-fill text-sm"></i>
                </div>
                (11) 91777-6227
              </a>
            </div>
          </aside>
        </div>
      </div>

      {/* ── Related Posts ── */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div
            ref={relatedHeaderRef}
            className={`flex items-center justify-between mb-12 transition-all duration-700 ${relatedHeaderVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          >
            <h2 className="font-heading font-bold text-3xl text-secondary">Leia também</h2>
            <Link to="/blog" className="font-body text-sm font-semibold text-primary flex items-center gap-1 hover:gap-2 transition-all cursor-pointer whitespace-nowrap">
              Ver todos os artigos
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm"></i>
              </div>
            </Link>
          </div>

          <div ref={relatedGridRef} className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {relatedPosts.map((rp, index) => (
              <Link
                key={rp.id}
                to={`/blog/${rp.slug}`}
                className={`group cursor-pointer transition-all duration-700 ease-out ${
                  index < relatedVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: index < relatedVisible ? `${index * 100}ms` : '0ms' }}
              >
                <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-primary/25 hover:-translate-y-1 transition-all duration-300 h-full flex flex-col">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={rp.image}
                      alt={rp.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                    <div className="absolute top-3 left-3 bg-primary text-white px-3 py-1 rounded-full font-body text-xs font-bold uppercase">
                      {rp.category}
                    </div>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="font-heading font-bold text-base text-secondary group-hover:text-primary transition-colors mb-2 line-clamp-2 flex-1">
                      {rp.title}
                    </h3>
                    <p className="font-body text-sm text-gray-500 line-clamp-2 mb-4">
                      {rp.excerpt}
                    </p>
                    <span className="font-body text-xs text-primary font-semibold flex items-center gap-1 group-hover:gap-2 transition-all mt-auto">
                      Ler artigo
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-arrow-right-line text-xs"></i>
                      </div>
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {post && /academia|aula|yoga|spinning|danca|dança|fitness|meditac|sao-paulo|são paulo|treinamento|corrida|escola|curso|recorrente|mensal/i.test(post.slug + ' ' + post.category + ' ' + post.title) && (
        <PacotesBox texto="Para atividades recorrentes, como aulas, treinamentos e eventos frequentes em São Paulo e região, o pacote mensal ou o pacote de eventos garante fones e equipe reservados nas suas datas, com valor fechado e economia em relação à contratação avulsa." />
      )}
      <Footer />
    </div>
  );
}
